// src/features/botHealth/BotHealthPage.tsx
import { useBotHealth, type SyncRun } from './useBotHealth';
import { formatRelative } from '../../lib/formatTime';
import './BotHealthPage.css';

// Turns a raw sync_type like "market_history_1d" into "Market History 1d"
function formatSyncTypeName(syncType: string) {
  return syncType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function statusBadge(run: SyncRun) {
  if (run.success === true) return <span className="status-badge status-badge--fresh">Healthy</span>;
  if (run.success === false) return <span className="status-badge status-badge--error">Failing</span>;
  return <span className="status-badge status-badge--warning">Unknown</span>;
}

// "3h 24m" rather than the relative "3h ago" style — uptime reads
// better as a duration than as a past event.
function formatUptime(startedAt: string) {
  const totalMinutes = Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function PollerRow({ run, index }: { run: SyncRun; index: number }) {
  return (
    <tr className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
      <td className="bot-health-table__name">{formatSyncTypeName(run.sync_type)}</td>
      <td>{statusBadge(run)}</td>
      <td className="bot-health-table__mono">{formatRelative(run.started_at)}</td>
      <td className="bot-health-table__mono">
        {run.items_saved != null ? `${run.items_saved.toLocaleString()} items` : '—'}
      </td>
      <td className="bot-health-table__mono">{run.attempt_count > 1 ? `${run.attempt_count} attempts` : '—'}</td>
      <td className="bot-health-table__error">{run.error_message ?? '—'}</td>
    </tr>
  );
}

export function BotHealthPage() {
  const { data, isLoading, error, dataUpdatedAt } = useBotHealth();
  const runs = data?.pollers;
  const botStatus = data?.botStatus;

  const healthyCount = runs?.filter((r) => r.success === true).length ?? 0;
  const failingCount = runs?.filter((r) => r.success === false).length ?? 0;

  // A stale heartbeat (much older than the bot's own 30s interval)
  // means the process itself may have crashed or hung, not merely
  // that Discord disconnected — worth distinguishing from the
  // discord_status the bot last reported before going quiet.
  const heartbeatStale = botStatus ? Date.now() - new Date(botStatus.last_heartbeat_at).getTime() > 90_000 : true;
  const connectionLabel = !botStatus ? 'Unknown' : heartbeatStale ? 'Unresponsive' : botStatus.discord_status === 'ready' ? 'Connected' : 'Disconnected';
  const connectionBadgeClass = !botStatus || heartbeatStale || botStatus.discord_status !== 'ready' ? 'status-badge--error' : 'status-badge--fresh';

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Poller status — bot-wide, not per-server</p>
        <h1 className="page-header__title">Bot Health</h1>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <p className="stat-card__label">Connection</p>
          <p className="stat-card__value"><span className={`status-badge ${connectionBadgeClass}`}>{connectionLabel}</span></p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Uptime</p>
          <p className="stat-card__value">{botStatus ? formatUptime(botStatus.started_at) : '—'}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Active servers</p>
          <p className="stat-card__value">{data?.guildCounts.active ?? '—'}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Removed servers</p>
          <p className="stat-card__value gold">{data?.guildCounts.removed ?? '—'}</p>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <p className="stat-card__label">Healthy pollers</p>
          <p className="stat-card__value positive">{healthyCount}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Failing pollers</p>
          <p className="stat-card__value" style={{ color: failingCount > 0 ? 'var(--negative)' : undefined }}>{failingCount}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Total tracked</p>
          <p className="stat-card__value">{runs?.length ?? 0}</p>
        </div>
      </div>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">
              {dataUpdatedAt ? `Refreshed ${formatRelative(new Date(dataUpdatedAt).toISOString())}` : ''}
            </p>
            <h2 className="card__title">Last run per poller</h2>
          </div>
        </header>
        <div className="card__content">
          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load poller status. Try refreshing.</p>}
          {runs?.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No poller runs recorded yet.</p>}
          {runs && runs.length > 0 && (
            <table className="bot-health-table">
              <thead>
                <tr>
                  <th>Poller</th><th>Status</th><th>Last run</th><th>Last result</th><th>Retries</th><th>Error</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run, index) => <PollerRow key={run.sync_type} run={run} index={index} />)}
              </tbody>
            </table>
          )}
        </div>
      </article>
    </>
  );
}
